import { createContext, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Account, Organization, Group, UserRole } from '@/types/icrop';

/**
 * State machine for the current host/slug resolution.
 *
 * - `apex`        — Marketing (`icrop.net`) or admin shell (`app.icrop.net`).
 *                   No workspace is bound to the URL.
 * - `loading`     — Resolving the slug → accounts row.
 * - `not-found`   — Slug has no matching account.
 * - `suspended`   — `accounts.subscription_status = 'suspended'`.
 * - `expired`     — Trial expired (`trialing` + `trial_ends_at < now`).
 * - `unavailable` — Account is soft-deleted or cancelled.
 * - `denied`      — User is logged in but lacks access to this workspace.
 * - `error`       — A transient failure (network/timeout) broke the
 *                   profile/membership lookup. Recoverable (retry), NOT
 *                   terminal like `denied`.
 * - `active`      — Workspace usable. `profile` is `null` pre-auth (renders
 *                   public login with workspace branding).
 */
export type WorkspaceState =
  | { kind: 'apex' }
  | { kind: 'loading' }
  | { kind: 'not-found'; slug: string }
  | { kind: 'suspended'; account: Account; reason: string | null }
  | { kind: 'expired'; account: Account }
  | { kind: 'unavailable'; account: Account; reason: 'deleted' | 'cancelled' }
  | { kind: 'denied'; account: Account }
  | { kind: 'error'; slug: string }
  | { kind: 'active'; account: Account; profile: WorkspaceProfile | null };

export interface WorkspaceProfile {
  id: string;
  account_id: string;
  email: string;
  role: UserRole;
  [key: string]: unknown;
}

export interface WorkspaceContextValue {
  // State-machine API (Phase 1)
  state: WorkspaceState;
  slug: string | null;

  // Legacy AccountContext API (Phase 4 will sweep call sites; alias temporary)
  account: Account | null;
  setAccount: (account: Account | null) => void;
  organizations: Organization[];
  groups: Group[];
  isLoading: boolean;
  error: Error | null;
  refreshAccount: () => void;
  switchAccount: (accountId: string) => Promise<Account>;
  getOrganizationsByGroup: (groupId: string) => Organization[];
  getStandaloneOrganizations: () => Organization[];
  isGroupsEnabled: () => boolean;
}

export const WorkspaceContext = createContext<WorkspaceContextValue | undefined>(undefined);

/**
 * Returns the full workspace context value (state machine + legacy account API).
 * For the legacy hook shape kept under `useAccount` and friends below.
 */
export function useWorkspace(): WorkspaceContextValue {
  const context = useContext(WorkspaceContext);
  if (context === undefined) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
}

/** Returns just the state-machine slice — for shells/lifecycle pages. */
export function useWorkspaceState(): WorkspaceState {
  return useWorkspace().state;
}

/* -------------------------------------------------------------------------- */
/* Legacy compatibility shims — keep ~26 import sites working without touching */
/* them. Phase 4 (multi-tenant cleanup) will rename callers and drop these.    */
/* -------------------------------------------------------------------------- */

/** @deprecated — use `useWorkspace()` directly. Kept for back-compat. */
export const useAccount = useWorkspace;

// Hook for super admin functionality (managing multiple accounts).
// Kept here because legacy callers (iCropDashboard) import it from this module.
export function useSuperAdmin() {
  const { data: accounts, isLoading } = useQuery({
    queryKey: ['all-accounts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('accounts')
        .select(`
          *,
          organizations_count:organizations(count),
          groups_count:groups(count),
          users_count:profiles(count)
        `)
        .order('name');

      if (error) throw error;
      return data;
    },
    staleTime: 10 * 60 * 1000,
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const createAccount = async (_accountData: {
    name: string;
    service_type: 'fsvp' | 'document_control';
    contact_email: string;
    contact_phone?: string;
  }) => {
    // Dead code — this function omits the required `slug` field (accounts.slug is
    // NOT NULL per migration 022). All account creation flows through
    // AccountCreationModal's own useMutation, which includes slug generation,
    // validation, and uniqueness checking. Do not call this method.
    throw new Error('Use AccountCreationModal mutation path, which includes slug generation and validation.');
  };

  return {
    accounts: accounts ?? [],
    isLoading,
    createAccount,
  };
}

// Hook for organization management
export function useOrganizations(accountId?: string) {
  const { account } = useWorkspace();
  const targetAccountId = accountId || account?.id;

  return useQuery({
    queryKey: ['organizations', targetAccountId],
    queryFn: async () => {
      if (!targetAccountId) return [];

      const { data, error } = await supabase
        .from('organizations')
        .select(`
          *,
          documents_count:documents(count)
        `)
        .eq('account_id', targetAccountId)
        .order('name');

      if (error) throw error;
      return data;
    },
    enabled: !!targetAccountId,
    staleTime: 5 * 60 * 1000,
  });
}

// Hook for group management
export function useGroups(accountId?: string) {
  const { account } = useWorkspace();
  const targetAccountId = accountId || account?.id;

  return useQuery({
    queryKey: ['groups', targetAccountId],
    queryFn: async () => {
      if (!targetAccountId) return [];

      const { data, error } = await supabase
        .from('groups')
        .select(`
          *,
          organizations:organizations(*),
          documents_count:documents(count)
        `)
        .eq('account_id', targetAccountId)
        .order('name');

      if (error) throw error;
      return data;
    },
    enabled: !!targetAccountId,
    staleTime: 5 * 60 * 1000,
  });
}
